import { supabase } from '../../config/supabaseClient';
import { ENV } from '../../config/env';

export interface Milestone {
  id: string;
  project_id: string;
  organization_id: string;
  name: string;
  description?: string;
  due_date: string;
  completed: boolean;
  completed_at?: string;
  color: string;
  created_at: string;
}

export interface ListMilestonesParams {
  organization_id: string;
  project_id?: string;
}

export interface MilestoneInput {
  organization_id: string;
  project_id: string;
  name: string;
  description?: string;
  due_date: string;
  completed?: boolean;
  completed_at?: string;
  color?: string;
}

async function getToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return session.access_token;
}

const API_BASE_URL = `${ENV.API_URL}/api/project-management/milestones`;

export const milestonesService = {
  async list(params: ListMilestonesParams): Promise<{ data: Milestone[]; error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });

      const response = await fetch(`${API_BASE_URL}?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      if (!response.ok) {
        return { data: [], error: result?.error || 'Failed to fetch milestones' };
      }

      return { data: (result.data || []) as Milestone[], error: null };
    } catch (err: any) {
      return { data: [], error: err.message || 'Network error' };
    }
  },

  async create(input: MilestoneInput): Promise<{ data: Milestone | null; error: any }> {
    try {
      const token = await getToken();

      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      const result = await response.json();
      if (!response.ok) {
        return { data: null, error: result?.error || 'Failed to create milestone' };
      }

      return { data: (result.data || null) as Milestone | null, error: null };
    } catch (err: any) {
      return { data: null, error: err.message || 'Network error' };
    }
  },

  async update(milestoneId: string, organizationId: string, updates: Partial<MilestoneInput>): Promise<{ data: Milestone | null; error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams({ organization_id: organizationId });

      const response = await fetch(`${API_BASE_URL}/${milestoneId}?${queryParams.toString()}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      const result = await response.json();
      if (!response.ok) {
        return { data: null, error: result?.error || 'Failed to update milestone' };
      }

      return { data: (result.data || null) as Milestone | null, error: null };
    } catch (err: any) {
      return { data: null, error: err.message || 'Network error' };
    }
  },

  async delete(milestoneId: string, organizationId: string): Promise<{ error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams({ organization_id: organizationId });

      const response = await fetch(`${API_BASE_URL}/${milestoneId}?${queryParams.toString()}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      if (!response.ok) {
        return { error: result?.error || 'Failed to delete milestone' };
      }

      return { error: null };
    } catch (err: any) {
      return { error: err.message || 'Network error' };
    }
  },
};

export default milestonesService;
